import React, { useState, useEffect } from 'react';
import { startOfMonth, endOfMonth } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SocialMediaPlannerService } from '../../services/socialMediaPlannerService';
import { SocialPost, SocialPlatform } from '../../types/socialMediaPlanner';
import { withErrorHandling } from '../../utils/errorHandler';
import toast from 'react-hot-toast';
import CalendarView from './components/socialMediaPlanner/CalendarView';
import QueueView from './components/socialMediaPlanner/QueueView';
import AnalyticsView from './components/socialMediaPlanner/AnalyticsView';
import MediaLibrary from './components/socialMediaPlanner/MediaLibrary';
import PostComposer from './components/socialMediaPlanner/PostComposer';
import SideNavigation from './components/socialMediaPlanner/SideNavigation';
import SocialAccountConnector from './components/socialMediaPlanner/SocialAccountConnector';
import CampaignManager from '../../components/social/CampaignManager';

/**
 * Social Media Planner
 * A comprehensive tool for planning, scheduling, and analyzing social media posts
 */
const SocialMediaPlanner: React.FC = () => {
  // State management
  const [activeView, setActiveView] = useState<'calendar' | 'queue' | 'analytics' | 'media' | 'campaigns' | 'accounts'>('calendar');
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [editingPost, setEditingPost] = useState<SocialPost | null>(null);
  
  // Services
  const { user } = useAuth();
  const socialMediaService = new SocialMediaPlannerService();
  const location = useLocation();
  const navigate = useNavigate();

  // helper to map DB scheduler job -> SocialPost for UI
  const mapLinkedInJobToPost = (job: any): SocialPost => {
    const statusMap: Record<string, SocialPost['status']> = {
      queued: 'scheduled',
      processing: 'scheduled',
      done: 'published',
      failed: 'failed',
    };
    return {
      id: job.id,
      user_id: job.user_id,
      content: job.content || '',
      platforms: ['linkedin'],
      scheduled_for: job.scheduled_for,
      status: statusMap[job.status] || 'scheduled',
      hashtags: [],
      media_urls: [],
    };
  };

  // Load posts on component mount
  useEffect(() => {
    const loadPosts = async () => {
      try {
        setIsLoading(true);
        if (!user) return;
        
        const userPosts = await socialMediaService.getPosts();
        // Also load DB-backed LinkedIn jobs for queue/calendar
        const jobs = await socialMediaService.fetchLinkedInJobs({ status: ['queued','processing'] });
        const mappedJobs: SocialPost[] = (jobs || []).map(mapLinkedInJobToPost);
        setPosts([...(userPosts || []), ...mappedJobs]);
      } catch (error) {
        console.error('Error loading posts:', error);
        toast.error('Failed to load your scheduled posts');
      } finally {
        setIsLoading(false);
      }
    };

    loadPosts();
  }, [user]);

  // Periodically refresh queued/processing LinkedIn jobs for queue accuracy
  useEffect(() => {
    if (!user) return;
    let timer: any;
    const refresh = async () => {
      try {
        const jobs = await socialMediaService.fetchLinkedInJobs({ status: ['queued','processing'] });
        const mapped: SocialPost[] = (jobs || []).map(mapLinkedInJobToPost);
        setPosts(prev => {
          const byId: Record<string, SocialPost> = {};
          // seed with prev
          for (const p of prev) {
            if (p.id) byId[p.id] = p;
          }
          // overlay mapped
          for (const m of mapped) {
            if (m.id) byId[m.id] = m;
          }
          return Object.values(byId);
        });
      } catch (e) {
        // silent; queue refresh is best-effort
      }
    };
    // initial and interval
    refresh();
    timer = setInterval(refresh, 30000);
    return () => { if (timer) clearInterval(timer); };
  }, [user]);

  // Periodically refresh posts from Supabase so status changes (e.g., draft -> published) are reflected
  useEffect(() => {
    if (!user) return;
    let timer: any;
    const refreshPosts = async () => {
      try {
        const latest = await socialMediaService.getPosts();
        setPosts(prev => {
          const byId: Record<string, SocialPost> = {};
          // start with previous so we keep any transient mapped jobs
          for (const p of prev) {
            if (p.id) byId[p.id] = p;
          }
          // overlay latest DB posts (these will carry updated status and URN)
          for (const p of latest || []) {
            if (p.id) byId[p.id] = p;
          }
          return Object.values(byId);
        });
      } catch (e) {
        // non-fatal
      }
    };
    // initial and interval refresh
    refreshPosts();
    timer = setInterval(refreshPosts, 20000);
    return () => { if (timer) clearInterval(timer); };
  }, [user]);

  // Fetch calendar jobs for the visible month when in calendar view
  useEffect(() => {
    if (!user) return;
    if (activeView !== 'calendar') return;
    const from = startOfMonth(selectedDate).toISOString();
    const to = endOfMonth(selectedDate).toISOString();
    const loadCalendar = async () => {
      try {
        const jobs = await socialMediaService.fetchLinkedInCalendar(from, to);
        const mapped: SocialPost[] = (jobs || []).map(mapLinkedInJobToPost);
        setPosts(prev => {
          const byId: Record<string, SocialPost> = {};
          for (const p of prev) {
            if (p.id) byId[p.id] = p;
          }
          for (const m of mapped) {
            if (m.id) byId[m.id] = m;
          }
          return Object.values(byId);
        });
      } catch (e) {
        // non-fatal; calendar still shows other posts
      }
    };
    loadCalendar();
  }, [user, activeView, selectedDate]);

  // Sync active view from query string (e.g., ?view=accounts)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const view = params.get('view');
    if (view === 'calendar' || view === 'queue' || view === 'analytics' || view === 'media' || view === 'campaigns' || view === 'accounts') {
      setActiveView(view as any);
    }
  }, [location.search]);

  // Handle chat CTA query params to open composer prefilled
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const compose = params.get('compose');
    if (!compose) return;

    // Parse inputs
    const contentParam = params.get('content') || '';
    const platformParam = (params.get('platform') || 'linkedin').toLowerCase();
    const whenParam = params.get('when'); // e.g., 'now' or ISO/parseable string

    const allowed: SocialPlatform[] = ['facebook','instagram','linkedin','pinterest','tiktok','youtube'];
    const platform: SocialPlatform = (allowed.includes(platformParam as SocialPlatform) ? platformParam : 'linkedin') as SocialPlatform;

    // Build initial post for the composer
    let scheduledISO: string | undefined;
    if (whenParam && whenParam.toLowerCase() !== 'now') {
      const t = Date.parse(whenParam);
      if (!Number.isNaN(t)) scheduledISO = new Date(t).toISOString();
    }

    const initialPost: SocialPost = {
      content: contentParam,
      platforms: [platform],
      status: scheduledISO ? 'scheduled' : 'draft',
      scheduled_for: scheduledISO || undefined,
      hashtags: [],
      media_urls: []
    };

    setEditingPost(initialPost);
    setIsComposerOpen(true);

    // Optional: switch to queue view for clearer context
    if (activeView !== 'queue') setActiveView('queue');

    // Clean URL to avoid re-trigger on rerenders
    const clean = new URLSearchParams(location.search);
    clean.delete('compose');
    clean.delete('content');
    clean.delete('platform');
    clean.delete('when');
    navigate({ pathname: location.pathname, search: clean.toString() ? `?${clean.toString()}` : '' }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Handle creating a new post
  const handleCreatePost = async (newPost: SocialPost) => {
    try {
      const savedPost = await socialMediaService.createPost(newPost);
      setPosts([...posts, savedPost]);
      setIsComposerOpen(false);
      toast.success('Post created successfully');
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error('Failed to create post');
    }
  };

  // Handle updating a post
  const handleUpdatePost = async (updatedPost: SocialPost) => {
    try {
      const savedPost = await socialMediaService.updatePost(updatedPost);
      setPosts(posts.map(p => p.id === savedPost.id ? savedPost : p));
      setEditingPost(null);
      setIsComposerOpen(false);
      toast.success('Post updated successfully');
    } catch (error) {
      console.error('Error updating post:', error);
      toast.error('Failed to update post');
    }
  };

  // Handle deleting a post
  const handleDeletePost = async (postId: string) => {
    try {
      await socialMediaService.deletePost(postId);
      setPosts(posts.filter(p => p.id !== postId));
      toast.success('Post deleted successfully');
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };
  
  // Handle rescheduling a post via drag-and-drop
  const handleReschedulePost = async (postId: string, newDate: Date) => {
    try {
      // Find the post to update
      const postToUpdate = posts.find(p => p.id === postId);
      if (!postToUpdate) {
        toast.error('Post not found');
        return;
      }
      
      // Create an updated post with the new scheduled date
      const updatedPost: SocialPost = {
        ...postToUpdate,
        scheduled_for: newDate.toISOString()
      };
      
      // Update the post using the existing service method
      const savedPost = await socialMediaService.updatePost(updatedPost);
      
      // Update the posts state
      setPosts(posts.map(p => p.id === savedPost.id ? savedPost : p));
      toast.success('Post rescheduled successfully');
    } catch (error) {
      console.error('Error rescheduling post:', error);
      toast.error('Failed to reschedule post');
    }
  };

  // Handle editing a post
  const handleEditPost = (post: SocialPost) => {
    setEditingPost(post);
    setIsComposerOpen(true);
  };

  // Save or update a post
  const handleSavePost = (post: SocialPost) => {
    if (editingPost) {
      handleUpdatePost({ ...post, id: editingPost.id });
    } else {
      handleCreatePost(post);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Sidebar Navigation */}
      <SideNavigation 
        activeView={activeView} 
        onChangeView={setActiveView} 
        onCreatePost={() => {
          setEditingPost(null);
          setIsComposerOpen(true);
        }}
      />
      
      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto p-4 bg-white dark:bg-gray-800">
        {/* Main view has been moved to the dynamic view area below */}
        
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-1 items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}
            
          {/* Dynamic View Area */}
          {!isLoading && (
            <div className="flex-1 bg-white p-6 shadow-sm rounded-lg overflow-hidden">
              {activeView === 'calendar' && (
                <CalendarView 
                  posts={posts} 
                  selectedDate={selectedDate}
                  onSelectDate={setSelectedDate}
                  onEditPost={handleEditPost}
                  onDeletePost={handleDeletePost}
                  onReschedulePost={handleReschedulePost}
                />
              )}
              
              {activeView === 'queue' && (
                <QueueView 
                  posts={posts.filter(p => (p.status === 'scheduled' || p.status === 'draft') && !p.external_post_urn)}
                  onEditPost={handleEditPost}
                  onDeletePost={handleDeletePost}
                />
              )}
              
              {activeView === 'analytics' && (
                <AnalyticsView posts={posts} />
              )}
              
              {activeView === 'media' && (
                <MediaLibrary />
              )}
              
              {activeView === 'campaigns' && (
                <CampaignManager className="h-full" />
              )}
              
              {activeView === 'accounts' && (
                <SocialAccountConnector />
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Post Composer Modal */}
      {isComposerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-3/4 max-w-4xl max-h-screen overflow-auto">
            <PostComposer 
              post={editingPost || undefined}
              onSave={handleSavePost}
              onLinkedInScheduledOptimistic={(job: { id: string; user_id?: string; platform: string; content: string; scheduled_for: string; status?: string }) => {
                // optimistic add to UI
                const optimistic = mapLinkedInJobToPost(job);
                setPosts(prev => [...prev, optimistic]);
              }}
              onClose={() => {
                setIsComposerOpen(false);
                setEditingPost(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialMediaPlanner;
